import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CatalogSearch } from '../models/catalog.models';
import { catchError, switchMap } from 'rxjs/operators';
import { GlueConfigurationService } from './glue-configuration.service';
import {
  IArchiveOrderResponse,
  ICartsApprovalResponse,
  IOrderById,
  IOrders,
  IOrderSearchFilter,
  IPendingOrder,
  IQuoteRequestAcceptRejectResponse,
} from '../models/order.models';
import { EQueryParams } from '../configurations/common';
import { IUploadFileResponse } from '../models/checkout.models';

@Injectable({
  providedIn: 'root',
})
export class OrdersService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly glueConfiguration: GlueConfigurationService = inject(GlueConfigurationService);

  private readonly mainUrl: string = this.glueConfiguration.getEndpointUrl();
  private readonly cartsUrl: string = '/carts';
  private readonly quoteRequestsUrl: string = '/quote-requests';

  private static handleOrderError(error: any): Promise<any> {
    return Promise.reject(error.message || error);
  }

  getCarts(): Observable<any> {
    return this.http.get<CatalogSearch>(`${this.mainUrl}/carts`).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  getCartsApproval(): Observable<ICartsApprovalResponse> {
    return this.http.get<IPendingOrder[]>(`${this.mainUrl}/carts-approval`).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  getCartForApprovalData(id: string): Observable<any> {
    const httpParamsForGetCartItems: { params: HttpParams } = {
      params: new HttpParams().set(
        'include',
        'items,concrete-products,concrete-product-image-sets,vouchers',
      ),
    };

    return this.http.get<any>(`${this.mainUrl}/carts-approval/${id}`, httpParamsForGetCartItems).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  getOrderById(id: string): Observable<IOrderById> {
    return this.http.get<IOrderById>(`${this.mainUrl}/orders/${id}`).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  getGuestOrderById(guestUserId: string, guestOrderId: string): Observable<IOrderById> {
    return this.http.get<IOrderById>(
      `${this.mainUrl}/guest-orders/${guestOrderId}`,
      {
        headers: (new HttpHeaders({'X-Anonymous-Customer-Unique-Id': guestUserId})),
      },
    ).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  getHybrisOrderById(id: string): Observable<IArchiveOrderResponse> {
    return this.http.get<IArchiveOrderResponse>(`${this.mainUrl}/customer-orders-history/${id}`).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  postDeclinePendingOrder(data: any): Observable<any> {
    return this.http.post<any>(`${this.mainUrl}/quote-decline`, data).pipe(
      catchError(OrdersService.handleOrderError));
  }

  deleteCart(cartId: string): Observable<any> {
    return this.http.delete<any>(`${this.mainUrl}${this.cartsUrl}/${cartId}`).pipe(
      catchError(OrdersService.handleOrderError));
  }

  getOrders(
    offset: number | undefined = undefined,
    limit: number | undefined = undefined,
    orderFilter: Partial<IOrderSearchFilter> = {},
  ): Observable<any> {
    let httpParams: HttpParams = new HttpParams();

    if (orderFilter.includeApproverOrders) {
      httpParams = httpParams
        .set('orderFilter[includeApproverOrders]', true);
    }

    if (orderFilter.isFinalized !== undefined) {
      httpParams = httpParams.set('orderFilter[isFinalized]', orderFilter.isFinalized);
    }

    if (orderFilter.workflow) {
      httpParams = httpParams.set('orderFilter[workflow]', orderFilter.workflow);
    }

    if ((offset === 0 || offset) && limit) {
      httpParams = httpParams
        .set(EQueryParams.OFFSET, offset)
        .set(EQueryParams.LIMIT, limit);
    }

    httpParams = httpParams.set(EQueryParams.SORT, '-created_at'); // minus sign means DESC direction (newest first)

    return this.http.get<IOrders>(`${this.mainUrl}/orders`, {params: httpParams});
  }

  getArchiveOrders(): Observable<IArchiveOrderResponse> {
    return this.http.get<IArchiveOrderResponse>(`${this.mainUrl}/customer-orders-history`).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  deleteItemFromOrder(cartId: any, itemId: any): Observable<any> {
    const url: string = `${this.mainUrl + this.cartsUrl}/${cartId}/items/${itemId}`;
    return this.http.delete<any>(url).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  postCancelOrder(_orderId: string, data: any): Observable<any> {
    const url: string = `${this.mainUrl}/orders-cancel`;
    return this.http.post<any>(url, data).pipe(
      catchError(OrdersService.handleOrderError));
  }

  getCartDetailPdfFile(cartId: string): Observable<any> {
    return this.http.get(this.mainUrl + `/carts/${cartId}/export-to-pdf`, {responseType: 'blob', observe: 'response'})
      .pipe(catchError(OrdersService.handleOrderError));
  }

  getCartGenerateQuotePdfFile(cartId: string, isPreview: boolean, isDownload: boolean = false): Observable<any> {
    return this.http.get(
      this.mainUrl + `/carts/${cartId}/generate-quote`,
      {
        responseType: 'blob',
        observe: 'response',
        params: new HttpParams()
          .set('preview', isPreview ? 1 : 0)
          .set('download', isDownload ? 1 : 0),
      },
    ).pipe(catchError(OrdersService.handleOrderError));
  }

  getOrderDetailPdfFile(orderReference: string): Observable<any> {
    return this.http.get(`${this.mainUrl}/orders/${orderReference}/export-to-pdf`, {
      responseType: 'blob',
      observe: 'response',
    }).pipe(catchError(OrdersService.handleOrderError));
  }

  postOrdersHistoryExcelFile(activeOrdersReferences: string[]): Observable<HttpResponse<Blob>> {
    const body: any = {
      'data': {
        'type': 'orders-export-to-excel',
        'attributes': {
          'orderReferences': activeOrdersReferences,
        },
      },
    };
    return this.http.post<any>(`${this.mainUrl}/orders-export-to-excel`, body, {
      responseType: 'blob' as 'json',
      observe: 'response',
    }).pipe(catchError(OrdersService.handleOrderError));
  }

  postHybrisOrdersHistoryExcelFile(activeOrdersReferences: string[]): Observable<HttpResponse<Blob>> {
    const body: any = {
      'data': {
        'type': 'customer-orders-history-export-to-excel',
        'attributes': {
          'orderReferences': activeOrdersReferences,
        },
      },
    };
    return this.http.post<any>(`${this.mainUrl}/customer-orders-history-export-to-excel`, body, {
      responseType: 'blob' as 'json',
      observe: 'response',
    }).pipe(catchError(OrdersService.handleOrderError));
  }

  getQuoteHistoryExcelFile(activeQuoteReferences: string[]): Observable<HttpResponse<Blob>> {
    return this.http.get<any>(`${this.mainUrl + this.quoteRequestsUrl}/export/excel/?quoteReferences=` + activeQuoteReferences.join(','), {
      responseType: 'blob' as 'json',
      observe: 'response',
    }).pipe(catchError(OrdersService.handleOrderError));
  }

  /**
   * Downloads the quote request document file.
   *
   * @param fileDownloadLink String - Direct link to requested file
   * @returns Observable<HttpResponse<Blob>>
   */
  getQuoteRequestDocumentFile(fileDownloadLink: string): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.mainUrl}${fileDownloadLink}`,
      {
        responseType: 'blob',
        observe: 'response',
      },
    ).pipe(catchError(OrdersService.handleOrderError));
  }

 /**
  * Changes the status of a quote request version.
  *
  * @param quoteRequestId {string} - ID of the quote request
  * @param status {string} - New status to set
  * @param reason {string | undefined} - Reason for status change (optional)
  * @returns Observable<IQuoteRequestCancelRejectResponse>
  */
  acceptOrRejectRequestQuoteVersion(quoteRequestId: string, status: string, reason: string | undefined): Observable<IQuoteRequestAcceptRejectResponse> {
    const body: any = {
      data: {
        type: 'process-quote-request',
        attributes: {
          quoteRequestId: quoteRequestId,
          status: status,
          ...(reason && { reason: reason }),
        },
      },
    };

    return this.http.post<IQuoteRequestAcceptRejectResponse>(`${this.mainUrl}/process-quote-request`, body).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  /**
   * Downloads Purchase order document from server
   *
   * @param {string} fileLink - The relative file link/path to the document
   * @returns {Observable<HttpResponse<Blob>>} Observable that emits HTTP response containing the file as a Blob
   */
  downloadPoDocument(fileLink: string): Observable<HttpResponse<Blob>> {
    return this.http.get(
      `${this.mainUrl}/${fileLink}`,
      {
        responseType: 'blob',
        observe: 'response',
      },
    ).pipe(catchError(OrdersService.handleOrderError));
  }

  /**
   * Endpoint to delete uploaded PO documents to quote.
   *
   * @param quoteReference
   * @returns
   */
  deleteUploadedPODocuments(quoteReference: string): Observable<any> {
    return this.http.delete<any>(`${this.mainUrl}/uploaded-files-by-quote-reference/${quoteReference}`).pipe(
      catchError(OrdersService.handleOrderError),
    );
  }

  /**
   * Upload PO document.
   * Converts file to base64 before sending.
   *
   * @param {File} file - File to upload
   * @param {string} quoteReference - Quote ID to associate the document with
   * @returns {Observable<any>} - Observable with upload response
   */
  uploadPODocument(file: File, quoteReference: string): Observable<IUploadFileResponse> {
    return this._convertFileToBase64(file).pipe(
      switchMap(base64Content => {
        const body: any = {
          data: {
            type: 'upload-files',
            attributes: {
              fileName: file.name,
              quoteReference: quoteReference,
              fileContent: base64Content,
            },
          },
        };

        return this.http.post<IUploadFileResponse>(`${this.mainUrl}/upload-files`, body).pipe(
          catchError(OrdersService.handleOrderError),
        );
      }),
    );
  }

  /**
   * Convert File to base64 string.
   *
   * @private
   * @param {File} file - File to convert
   * @returns {Observable<string>} - Observable that emits base64 string
   */
  private _convertFileToBase64(file: File): Observable<string> {
    return new Observable<string>(observer => {
      const reader: FileReader = new FileReader();

      reader.onload = () => {
        const base64String: string = reader.result as string;
        observer.next(base64String);
        observer.complete();
      };

      reader.onerror = (error: any) => {
        observer.error(error);
      };

      reader.readAsDataURL(file);
    });
  }
}
